import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import {
  ArrowBackRounded,
  WarningAmberRounded,
  CheckCircleOutlineRounded,
  CloseOutlined,
} from "@mui/icons-material";
import axiosInstance from "../../config/axiosInstance";
import { USER_API } from "../../Utils/constants/apis.urls";

function readStoredUser() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }
  try {
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
}

export default function DeleteAccountPage() {
  const navigate = useNavigate();

  const storedUser = readStoredUser();
  const isLoggedIn = !!storedUser;

  const [email, setEmail] = useState(storedUser?.email || "");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success' | 'error', message: '' }
  
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const isConfirmTextValid = confirmText.trim().toLowerCase() === "confirm delete";

  useEffect(() => {
    if (confirmModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [confirmModalOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && confirmModalOpen) {
        setConfirmModalOpen(false);
        setConfirmText("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [confirmModalOpen]);

  // Redirect to login if user is not logged in and has not just successfully deleted their account
  useEffect(() => {
    if (!isLoggedIn && !isDeleted) {
      navigate("/login");
    }
  }, [isLoggedIn, isDeleted, navigate]);

  // Auto-hide message banner after 10 seconds (only for errors, success will redirect)
  useEffect(() => {
    if (result && result.type !== "success") {
      const timer = setTimeout(() => {
        setResult(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [result]);

  const canSubmit = email.trim() && confirmed && !loading;

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmModalOpen(false);
    setLoading(true);
    setResult(null);

    try {
      const response = await axiosInstance.post(USER_API.DELETE_ACCOUNT, {
        email: email.trim(),
        password: "",
        confirm_text: confirmText.trim().toUpperCase(),
      });

      setIsDeleted(true);

      // Clear storage
      localStorage.clear();
      sessionStorage.clear();
      window.dispatchEvent(new Event("user-profile-updated"));

      setResult({
        type: "success",
        message:
          response.data?.message ||
          "Your account has been deactivated. You can recover your account within 30 days by simply logging back in. Redirecting to login page...",
      });

      localStorage.clear();
      sessionStorage.clear();
      window.dispatchEvent(new Event("user-profile-updated"));

      setEmail("");
      setConfirmText("");
      setConfirmed(false);

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to process your request. Please try again or contact support.";
      setResult({ type: "error", message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navbar />

      <div className="flex justify-center px-5 lg:py-12 py-8">
        <main className="max-w-[1280px] w-full">
          <button
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate("/");
              }
            }}
            className="flex items-center gap-1.5 appearance-none border-none bg-transparent xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-semibold cursor-pointer md:mb-6 mb-4"
          >
            <ArrowBackRounded sx={{ fontSize: 20 }} /> Back to Home
          </button>

          <header className="flex flex-col gap-2">
            <h1 className="xl:text-6xl md:text-5xl text-[40px] leading-[0.98] font-bold tracking-[-0.03em]">
              Delete Account
            </h1>
            <p className="text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-medium">
              We're sorry to see you go. Use this form to request deletion
              of your The FormGuru account. Your account will be deactivated immediately, and you can recover it within 30 days by logging back in. After 30 days, the deletion becomes permanent.
            </p>
          </header>

          <div className="h-[1px] bg-[var(--color-border)] my-6" />

          <div className="flex flex-col">
            <Section number="1" title="What happens when you delete your account">
              <p>
                Your account will be deactivated immediately, and you will have 30 days to recover it. If not recovered, the following data will be <strong>permanently removed</strong> from our systems after 30 days:
              </p>
              <BulletList
                items={[
                  "Your account profile — name, email address, and login credentials.",
                  "Subscription records — plan details, billing history, and payment metadata.",
                  "Notification data — device tokens, notification preferences, and notification history.",
                  "Usage data — dashboard interaction logs, race query history, and analytics data.",
                ]}
              />
              <p>
                Some data may be retained for a limited period as required by
                applicable laws (e.g., financial records for tax/audit
                compliance). Once any legal retention period has passed, all
                remaining data will be permanently erased.
              </p>
            </Section>

            <Section number="2" title="Before you proceed">
              <p>Please consider the following before requesting deletion:</p>
              <BulletList
                items={[
                  "Account deletion is permanent after a 30-day grace period. During these 30 days, you can log back in at any time to recover your account and restore your data.",
                  "If you have an active subscription, it will be cancelled and no refund will be issued for the remaining period.",
                  "You will lose access to all race predictions, analytics, and historical data.",
                  "If you change your mind, you can recover your account by simply logging back in within 30 days.",
                ]}
              />
            </Section>

            <div className="flex flex-col gap-2">
              <h2 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em] flex items-center gap-2">
                <span className="tabular">3.</span>
                Request Account Deletion
              </h2>
              <div className="flex flex-col gap-2 xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                <p>
                  To verify your identity and process the deletion, please click the checkbox and confirm.
                </p>
              </div>

              {result && (
                <div
                  className={`flex items-center gap-3 mt-4 p-4 w-fit rounded-[var(--radius-md)] border ${
                    result.type === "success"
                      ? "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]"
                      : "border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]"
                  }`}
                >
                  {result.type === "success" ? (
                    <CheckCircleOutlineRounded
                      sx={{ fontSize: 20, flexShrink: 0 }}
                    />
                  ) : (
                    <WarningAmberRounded
                      sx={{ fontSize: 20, flexShrink: 0 }}
                    />
                  )}
                  <p className="xl:text-base md:text-sm text-xs font-medium m-0">
                    {result.message}
                  </p>
                </div>
              )}

              <form
                onSubmit={handleFormSubmit}
                className="flex flex-col gap-5 mt-4 max-w-[520px]"
              >
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="delete-email"
                    className="text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase tracking-[0.04em]"
                  >
                    Registered Email Address
                  </label>
                  <input
                    id="delete-email"
                    type="email"
                    required
                    readOnly
                    value={email}
                    className="w-full px-4 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none opacity-60 cursor-not-allowed"
                  />
                </div>

                <label
                  htmlFor="delete-confirm"
                  className="flex items-start gap-3 cursor-pointer select-none"
                >
                  <input
                    id="delete-confirm"
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="w-5 h-5 rounded-sm accent-[var(--color-primary)] cursor-pointer flex-shrink-0"
                  />
                  <span className="xl:text-sm text-xs text-[var(--color-text-muted)] font-medium">
                    I understand that this action will deactivate my account immediately. I can recover my account within 30 days by logging back in. After 30 days, this action becomes <strong className="text-[var(--color-accent-red)]">permanent and irreversible</strong>, and all my account data will be permanently deleted.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none p-3 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] ${
                    canSubmit
                      ? "cursor-pointer"
                      : "cursor-not-allowed"}`}
                >
                  {loading ? "Delete Account Data..." : "Delete My Account"}
                </button>
              </form>
            </div>

            <div className="h-[1px] bg-[var(--color-border)] my-6" />

            <Section number="4" showBorder={false} title="Need Help?">
              <p>
                If you are having trouble with your account or would like to
                discuss your options before deleting, please contact our support
                team at{" "}
                <a
                  href="mailto:service@formguru.com.au"
                  className="text-[var(--color-primary)] font-semibold"
                >
                  service@formguru.com.au
                </a>
                . We're happy to help.
              </p>
            </Section>
          </div>
        </main>
      </div>

      {confirmModalOpen && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center p-4 backdrop-blur-[16px]"
          onClick={() => {
            setConfirmModalOpen(false);
            setConfirmText("");
          }}
          role="dialog"
        >
          <div
            className="relative w-full max-w-lg bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] overflow-hidden text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                setConfirmModalOpen(false);
                setConfirmText("");
              }}
              className="absolute top-4 right-4 w-8 h-8 inline-flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] border-none cursor-pointer"
            >
              <CloseOutlined sx={{ fontSize: 20 }} />
            </button>
            <div className="px-4 py-8">
              <h2 className="xl:text-2xl md:text-xl text-lg font-bold text-white text-left mb-2">Confirm Account Deletion</h2>
              <p className="text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs text-left mb-4">
                Are you sure you want to delete your account <strong>{email}</strong>? Your account will be deactivated immediately, and you can recover it within 30 days by logging back in. After 30 days, your account and all associated data will be permanently deleted and cannot be recovered.
              </p>

              <div className="mb-5">
                <label htmlFor="confirm_text" className="md:text-sm text-xs label-caps text-left block mb-2">
                  TYPE "confirm delete" TO CONFIRM
                </label>
                <input
                  id="confirm_text"
                  type="text"
                  autoComplete="off"
                  className="w-full py-3 px-4 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[var(--radius-md)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none"
                  placeholder="confirm delete"
                  value={confirmText}
                  onChange={(e) => {
                    setConfirmText(e.target.value);
                  }}
                />
              </div>

              <div className="flex justify-end gap-3 md:flex-row flex-col-reverse mt-6">
                <button
                  type="button"
                  className="appearance-none md:w-auto w-full xl:text-base md:text-sm text-xs bg-transparent border border-[var(--color-border)] text-[var(--color-text)] p-3 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] cursor-pointer"
                  onClick={() => {
                    setConfirmModalOpen(false);
                    setConfirmText("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!isConfirmTextValid}
                  className="appearance-none md:w-auto w-full xl:text-base md:text-sm text-xs bg-[rgba(255,69,0,0.12)] border border-[rgba(255,69,0,0.25)] text-[var(--color-accent-red)] p-3 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleConfirmDelete}
                >
                  {loading ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Section({ number, showBorder = true, title, children }) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <h2 className="xl:text-xl md:text-lg text-base font-bold tracking-[-0.01em] flex items-center gap-2">
          <span className="tabular">{number}.</span>
          {title}
        </h2>
        <div className="flex flex-col gap-2 xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">
          {children}
        </div>
      </div>

      {showBorder && <div className="h-[1px] bg-[var(--color-border)] my-6" />}
    </>
  );
}

function BulletList({ items }) {
  return (
    <ul className="flex pl-3 flex-col gap-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-3">
          <span className="text-[var(--color-primary)] md:text-xl text-lg flex-shrink-0">&bull;</span>
          <span className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] font-medium">{item}</span>
        </li>
      ))}
    </ul>
  );
}
